/**
 * columns.js — Summary Case Table: DataGrid keys, column visibility, and XLS column metadata.
 *
 * THREE CONCERNS, CLEARLY SEPARATED:
 * ① DataGrid field keys  — `INTERNAL_GRID_KEYS`, `TOWN_GRID_KEYS`, `getKeysForView(townView, user)`
 *    Feed the row pipeline and `createColumnDefinitions()` in `config/columnDefinitions.js`.
 *    These values are the top level keys that build out the datagrid. if a value does not exist here, it will not be shown
 *    in the datagrid as a selectable column. Do NOT use these for spreadsheet exports.
 *
 * ② Column visibility models  — saved layouts come from Propriety.user_columns (see `useColumnLayout`);
 *    `INTERNAL_COLUMN_VISIBILITY` / `TOWN_COLUMN_VISIBILITY` apply until they load, and
 *    `getDynamicColumnVisibility()` layers the row / screen driven hides on top.
 *
 * ③ Spreadsheet (XLS) keys  — `INTERNAL_XLS_KEYS`, `TOWN_VIEW_XLS_KEYS`, `getTownViewXLSKeys()` (pass `user` for link permission parity with the grid)
 *    Feed `generateXLSData` in `Offers/s3UploadService.js` and the download button.
 *    Do NOT use these for the DataGrid.
 */

import { CASE_REVIEW_COLUMNS } from '../../../../common/mappings';

const summaryColumns = CASE_REVIEW_COLUMNS.filter((col) => col.summary);

// =============================================================================
// ① DataGrid field keys
// =============================================================================

/** Fields present in both the internal and town DataGrid views. */
const BASE_GRID_KEYS = [
    'Address',
    'PhotoURL',
    // 'Muni', // this is not defined in the column definitions currently
    'ClientName',
    'SCARIndexNumber',
    'SCARHearingDate',
    'SDName',
    'PropertyAssessment',
    'EQ',
    'RAR',
    'IFMV',
    'RecentSale',
    // 'SubjectMarketValue', // not used, propriety workup represents this now
    'OfferValue',
    'PctReduction',
    'RequestedAV',
    'PortfolioOffer',
    'PortfolioRequestedAV',
    'TownOffer',
    'TownNotes',
    'TownReqAV',
    'AventineNote',
  ];
  
  /** All field keys used by the internal (non-town) DataGrid. */
  export const INTERNAL_GRID_KEYS = [
    ...BASE_GRID_KEYS,
    'parcel_id',
    'SalePrice',
    'SaleDate',
    ...summaryColumns.map((col) => col.field),

    // Workup internal only cols
    'ManualReview',
    'RunDate',
    'CompsCount',
    'ProprietyWorkup',
    'CaseStrength',
    'CaseNotes',

    'OfferDiff',

    'Taxes',
    'RepID',
    'status',


    // Add any internal-only grid fields here
  ];
  
  /** All field keys used by the town DataGrid (ProprietyWorkup omitted; added when shared-workup links allow it). */
  export const TOWN_GRID_KEYS = [
    'parcel_id',
    ...BASE_GRID_KEYS,
    'TownResponse',
    // 'TownNotes',
    
    // These are appended conditionally below, as is the workup based on how link is permissioned
    // 'Comp1',
    // 'Comp2',
    // 'Comp3',
    // 'Comp4',
    // 'Comp5',
    // ProprietyWorkup
  ];
  
  /**
   * Returns the correct set of DataGrid field keys for the current view.
   * Pass `user` from `useUser()` for town view — this module cannot call hooks or read context itself.
   */
  export const getKeysForView = (townView, user) => {
    if (!townView) return INTERNAL_GRID_KEYS;
    const linkSharesWorkups =
      Boolean(user?.isLinkAuthenticated) && Boolean(user?.shareWorkups);
    const linkShowsGridComps =
      Boolean(user?.isLinkAuthenticated) && Boolean(user?.ShowGridComps);
    const extraKeys = [
      ...(linkSharesWorkups ? ['ProprietyWorkup'] : []),
      ...(linkShowsGridComps ? ['Comp1', 'Comp2', 'Comp3', 'Comp4', 'Comp5'] : []),
    ];
    return [...TOWN_GRID_KEYS, ...extraKeys];
  };
  
  
  // =============================================================================
  // ② Column visibility models
  // =============================================================================
  
  /**
   * Static default hidden columns shared by both views.
   * These are columns that exist in the grid but start hidden and can be
   * toggled by the user via the column-visibility menu.
   */
  const SHARED_HIDDEN_COLUMNS = {
    RepID:        false,
    SCARHearingDate: false,
    ClientName:   false,
    SalePrice:    false,
    SaleDate:     false,
    ...Object.fromEntries(summaryColumns.filter((col) => col.hidden).map((col) => [col.field, false])),
    RunDate:      false,
    ManualReview: false,
    OfferDiff:    false,
    Taxes:        false,
    SDName:       false,
  };
  
  /** Static default visibility for the internal DataGrid. */
  export const INTERNAL_COLUMN_VISIBILITY = {
    ...SHARED_HIDDEN_COLUMNS,
    CompsCount: false,
    PropertyAssessment: false,
    RequestedAV: true,
    // Internal-view-specific overrides (add here as needed)
    
    // Do we want to hide town notes to clean up teh summary grid?
    // TownNotes: false,
  };
  
  /** Static default visibility for the town DataGrid. */
  export const TOWN_COLUMN_VISIBILITY = {
    ...SHARED_HIDDEN_COLUMNS,
    // Town-view-specific overrides (add here as needed) - the internal datagrid field name not header.

    PhotoURL: false,
    parcel_id: false,
    CaseStrength: false,
    TownOffer: true,
    RequestedAV: true,
    TownReqAV: true,
    PropertyAssessment: true,
  // Default-hide secondary comps when they are enabled for link users.
  Comp3: false,
  Comp4: false,
  Comp5: false,
  };
  
  /**
   * Hides driven by row data, the AV toggle and screen size, layered over the saved layout.
   * These only hide columns, so a saved layout is never forced open. CaseNotes needs no rule here:
   * `createColumnDefinitions` only builds it when a row has a note.
   *
   * @param {object[]} rows
   * @param {boolean}  [avToggleActive=false]
   * @param {boolean}  [townView=false]
   * @param {boolean}  [compactScreen=false] — below the xl breakpoint: hide TownReqAV on the internal grid.
   *   RequestedAV stays visible. Town view keeps RequestedAV and TownReqAV visible.
   */
  export const getDynamicColumnVisibility = (rows, avToggleActive = false, townView = false, compactScreen = false) => {
    const hasScarId = rows?.some((row) => {
      const s = String(row?.SCARIndexNumber ?? '').trim();
      return s !== '' && s.toLowerCase() !== 'none';
    });
    return {
      ...(!hasScarId && { SCARIndexNumber: false }),
      // AV mode repurposes IFMV as assessment, so internal hides the duplicate PropertyAssessment column; town keeps both.
      ...(avToggleActive && { PortfolioRequestedAV: false, ...(!townView && { PropertyAssessment: false }) }),
      ...(compactScreen && !townView && { TownReqAV: false }),
    };
  };
  
  
  // =============================================================================
  // ③ Spreadsheet (XLS) keys
  // =============================================================================
  
  /** Keys for the internal "New template" spreadsheet download. */
  export const INTERNAL_XLS_KEYS = [
    'parcel_id',
    'Year',
    'Address',
    'Muni',
    'PropertyZip',
    'ClientName',
    'SCARIndexNumber',
    'SDName',
    'RunDate',
    'ManualReview',
    'PropertyAssessment',
    'RAR',
    'EQ',
    'IFMV',
    'SalePrice',
    'SaleDate',
    'RecentSale',
    'CompsCount',
    'SubjectMarketValue',
    'CaseStrength',
    'OfferValue',
    'PctReduction',
    'RequestedAV',
    'TownOffer',
    'PortfolioOffer',
    'PortfolioRequestedAV',
    'AventineNote',
    'InternalNote',
    'Status',
    'RepID',
    'Workup',
    'Comp1',
    'Comp2',
    'Comp3',
    'Comp4',
    'Comp5',
  ];
  
  /** @deprecated Use `INTERNAL_XLS_KEYS`; kept for existing imports. */
  export const STANDARD_XLS_KEYS = INTERNAL_XLS_KEYS;
  
  /** Keys for the town / offers spreadsheet download (full set). */
  export const TOWN_VIEW_XLS_KEYS = [
    'parcel_id',
    'ClientName',
    'SCARIndexNumber',
    'SDName',
    'Address',
    'RecentSale',
    'PropertyAssessment',
    'LOA',
    'IFMV',
    'SubjectMarketValue',
    'OfferValue',
    'RequestedAV',
    'PortfolioOffer',
    'PortfolioRequestedAV',
    'AventineNote',
    'Status',
    'TownResponse',
    'TownCounter',
    'TownNotes',
    'Workup',
    'Comp1',
    'Comp2',
    'Comp3',
    'Comp4',
    'Comp5',
    'Year',
    'CaseType',
  ];
  
  /**
   * Keys that are stripped from the town XLS export when comps are excluded (non–link users only).
   * Link users use `TOWN_XLS_LINK_GRID_COMP_KEYS` / `TOWN_XLS_LINK_WORKUP_KEYS` instead.
   */
  const TOWN_XLS_COMP_DEPENDENT_KEYS = [
    'SubjectMarketValue',
    'Workup',
    'Comp1',
    'Comp2',
    'Comp3',
    'Comp4',
    'Comp5',
  ];

  const TOWN_XLS_LINK_GRID_COMP_KEYS = ['Comp1', 'Comp2', 'Comp3', 'Comp4', 'Comp5'];
  const TOWN_XLS_LINK_WORKUP_KEYS = ['SubjectMarketValue', 'Workup'];

  /**
   * Returns the town-view XLS key list, optionally filtering comps / workup columns.
   *
   * Magic-link town users: same flags as the grid ({@link getKeysForView}) — `ShowGridComps` for Comp1–5,
   * `shareWorkups` for SubjectMarketValue + Workup spreadsheet columns.
   *
   * @param {{ user?: object, includeCompsInOffersSheet?: boolean, workupOptions?: { includeWorkups?: boolean, type?: string } }} [options]
   *   - `user` — from `useUser()`; when `isLinkAuthenticated`, comp/workup columns follow JWT flags.
   *   - `includeCompsInOffersSheet` — non-link: when `false`, removes {@link TOWN_XLS_COMP_DEPENDENT_KEYS}.
   *   - `workupOptions.type === 'individual'` — non-link: keep Workup column (spreadsheet links).
   */
  export const getTownViewXLSKeys = (options = {}) => {
    const user = options.user;
    const isLink = Boolean(user?.isLinkAuthenticated);

    let keys = [...TOWN_VIEW_XLS_KEYS];

    if (isLink) {
      if (!user?.ShowGridComps) {
        keys = keys.filter((k) => !TOWN_XLS_LINK_GRID_COMP_KEYS.includes(k));
      }
      if (!user?.shareWorkups) {
        keys = keys.filter((k) => !TOWN_XLS_LINK_WORKUP_KEYS.includes(k));
      }
      return keys;
    }

    const includeComps = options.includeCompsInOffersSheet !== false;
    const wo = options.workupOptions;
    const includeWorkupKey = wo === undefined ? true : wo.type === 'individual';

    if (!includeComps) {
      keys = keys.filter((k) => !TOWN_XLS_COMP_DEPENDENT_KEYS.includes(k));
    }

    if (!includeWorkupKey) {
      keys = keys.filter((k) => k !== 'Workup');
    }

    return keys;
  };
  
  
  // =============================================================================
  // ④ Shared spreadsheet column config + DataGrid table props
  // =============================================================================
  
  /**
   * Per-column formatting, width, and header metadata used by the XLS export layer.
   * Keyed by view: `COLUMN_CONFIG.internal` / `COLUMN_CONFIG.townView`.
   */
  export const COLUMN_CONFIG = {
    internal: {
      parcel_id:          { width: 15, header: 'Parcel ID', hidden: false },
      Year:               { width: 8,  header: 'Year', format: '0' },
      Address:            { width: 25 },
      Muni:               { width: 8,  header: 'Town' },
      PropertyZip:        { width: 10, header: 'Zip', format: '00000' },
      ClientName:         { width: 20, header: 'Client Name' },
      SCARIndexNumber:    { header: 'Index Number' },
      SDName:             { width: 18, header: 'School District' },
      RunDate:            { width: 14, header: 'Workup run' },
      ManualReview:       { width: 10, header: 'Manual review' },
      PropertyAssessment: { format: '#,##0',   width: 10, header: 'Assessment' },
      RAR:                { format: '0.00%',   width: 8,  header: 'RAR' },
      EQ:                 { format: '0.00%',   width: 8,  header: 'EQ' },
      IFMV:               { format: '#,##0',   width: 12, header: 'Town Eq Value' },
      SalePrice:          { format: '#,##0',   width: 12, header: 'Sale Price' },
      SaleDate:           { header: 'Sale Date' },
      RecentSale:         {},
      CompsCount:         { format: '0',       width: 10, header: '# Comps' },
      SubjectMarketValue: { format: '#,##0',   width: 15, header: 'Aventine Workup' },
      CaseStrength:       { format: '0%',      width: 12, header: 'Case Strength' },
      OfferValue:         { format: '#,##0',   width: 12, header: 'Aventine Offer', bold: true, blueBackground: true },
      PortfolioOffer:     { format: '#,##0',   width: 12, header: 'Portfolio Offer' },
      RequestedAV:        { format: '#,##0',   width: 12, header: 'Requested AV' },
      TownOffer:          { format: '#,##0',   width: 12, header: 'Town Offer' },
      OfferDiff:          { format: '#,##0',   width: 12, header: 'Offer Diff' },
      PortfolioRequestedAV: { format: '#,##0', width: 12, header: 'Portfolio Requested AV' },
      PctReduction:       { format: '0.00%',   width: 10, header: 'Pct Reduction', formula: true },
      AventineNote:       { width: 20, header: 'Aventine Note' },
      InternalNote:       { width: 20, header: 'Internal Note' },
      Status:             { width: 10, header: 'Status' },
      RepID:              {},
      Comp1:              { width: 25, header: 'Comp 1' },
      Comp2:              { width: 25, header: 'Comp 2' },
      Comp3:              { width: 25, header: 'Comp 3' },
      Comp4:              { width: 25, header: 'Comp 4' },
      Comp5:              { width: 25, header: 'Comp 5' },
      Workup:             { width: 40, header: 'Workup' },
    },
  
    townView: {
      ClientName:           { width: 20, header: 'Client Name' },
      SCARIndexNumber:      { header: 'Index Number' },
      SDName:               { width: 18, header: 'School District' },
      // SCARHearingDate:      { width: 12, header: 'Court Date' },
      Address:              { width: 25 },
      RecentSale:           {},
      PropertyAssessment:   { format: '#,##0',   width: 10, header: 'Assessment' },
      LOA:                  { format: '0.00%',   width: 10, header: 'LOA' },
      IFMV:                 { format: '#,##0',   width: 12, header: 'Full MV' },
      SubjectMarketValue:   { format: '#,##0',   width: 15, header: 'Aventine Workup' },
      OfferValue:           { format: '#,##0',   width: 12, header: 'Aventine Offer' },
      PortfolioOffer:       { format: '#,##0',   width: 12, header: 'Portfolio Offer' },
      RequestedAV:          { format: '#,##0',   width: 12, header: 'Requested AV' },
      PortfolioRequestedAV: { format: '#,##0',   width: 12, header: 'Portfolio Requested AV' },
      Status:               { width: 10, header: 'Status' },
      AventineNote:         { width: 30, header: 'Aventine Note' },
      TownResponse:         { width: 14, header: 'Town Response' },
      TownCounter:          { format: '#,##0',   width: 12, header: 'Town Counter ($)' },
      PriorTownOffer:       { format: '#,##0',   width: 16, header: 'Prior Town Offer (AV)' },
      TownNotes:            { width: 30, header: 'Town Notes' },
      Comp1:                { width: 25, header: 'Comp 1' },
      Comp2:                { width: 25, header: 'Comp 2' },
      Comp3:                { width: 25, header: 'Comp 3' },
      Comp4:                { width: 25, header: 'Comp 4' },
      Comp5:                { width: 25, header: 'Comp 5' },
      Workup:               { width: 40, header: 'Workup' },
      parcel_id:            { width: 15, header: 'Parcel ID', hidden: false },
      Year:                 { width: 8,  header: 'Year', hidden: true, format: '0' },
      CaseType:             { width: 12, header: 'CaseType', hidden: true },
    },
  };
  
  /** Default props applied to every Summary Case DataGrid instance. */
  export const DEFAULT_TABLE_PROPS = {
    density: 'compact',
    disableRowSelectionOnClick: true,
    disableColumnFilter: true,
    disableVirtualization: false,
    autoHeight: false,
  };